import { createSlice } from "@reduxjs/toolkit";

const betSlipSlice = createSlice({
  name: "betSlip",
  initialState: {
    bets: [],
    totalOdds: 0,
    totalStake: 0,
    possibleWin: 0,
    maxWin: 50000, // Maximum win limit
    minOdds: 1.01, // Minimum odds allowed
    maxOdds: 1000, // Maximum odds allowed
    betType: "single", // single, multiple, system
    systemOptions: [], // For system bets like 2/3, 3/4, etc.
    isOpen: false, // Betslip UI state
    errors: [], // Validation errors
  },
  reducers: {
    addBet: (state, action) => {
      const { match, odd, market } = action.payload;

      // Validate odds value
      const oddValue = parseFloat(odd.value);
      if (
        isNaN(oddValue) ||
        oddValue < state.minOdds ||
        oddValue > state.maxOdds
      ) {
        state.errors.push(`Invalid odds value: ${odd.value}`);
        return;
      }

      // Check if the bet already exists
      const existingBetIndex = state.bets.findIndex(
        (bet) =>
          bet.match.id === match.id &&
          bet.odd.label === odd.label &&
          bet.market?.marketName === market?.marketName
      );

      // Check for conflicting bets (same match, same market, different selection)
      const conflictingBetIndex = state.bets.findIndex(
        (bet) =>
          bet.match.id === match.id &&
          bet.market?.marketName === market?.marketName &&
          bet.odd.label !== odd.label
      );

      if (existingBetIndex !== -1) {
        // Bet already exists - no action needed
        return;
      }

      if (conflictingBetIndex !== -1) {
        // Replace conflicting bet
        state.bets[conflictingBetIndex] = { match, odd, market };
      } else {
        // Add new bet
        state.bets.push({ match, odd, market });
      }

      // Open betslip when adding first bet
      if (state.bets.length === 1) {
        state.isOpen = true;
      }

      // Update bet type if needed
      if (state.bets.length > 1 && state.betType === "single") {
        state.betType = "multiple";
      }

      // Recalculate total odds
      updateTotalOdds(state);

      // Clear any previous errors
      state.errors = [];
    },

    removeBet: (state, action) => {
      const { match, odd, market } = action.payload;

      // Remove specific bet
      state.bets = state.bets.filter(
        (bet) =>
          !(
            bet.match.id === match.id &&
            bet.odd.label === odd.label &&
            bet.market?.marketName === market?.marketName
          )
      );

      // Update bet type if needed
      if (state.bets.length <= 1 && state.betType === "multiple") {
        state.betType = "single";
      }

      // If removing all system bets, clear system options
      if (state.bets.length < 2) {
        state.systemOptions = [];
      }

      // Recalculate total odds
      updateTotalOdds(state);
    },

    setStake: (state, action) => {
      const stake = parseFloat(action.payload);

      if (isNaN(stake) || stake < 0) {
        state.errors.push("Invalid stake amount");
        return;
      }

      state.totalStake = stake;

      // Calculate possible win based on bet type
      calculatePossibleWin(state);

      // Apply maximum win limit
      if (state.possibleWin > state.maxWin) {
        state.errors.push(`Maximum win limit is ${state.maxWin}`);
        // Adjust stake to match max win
        state.totalStake = state.maxWin / state.totalOdds;
        state.possibleWin = state.maxWin;
      }
    },

    setBetType: (state, action) => {
      const newBetType = action.payload;
      if (["single", "multiple", "system"].includes(newBetType)) {
        state.betType = newBetType;

        // Clear system options if not a system bet
        if (newBetType !== "system") {
          state.systemOptions = [];
        }

        // Recalculate possible win
        calculatePossibleWin(state);
      }
    },

    setSystemOptions: (state, action) => {
      state.systemOptions = action.payload;
      // Set bet type to system when system options are provided
      if (state.systemOptions.length > 0) {
        state.betType = "system";
      }
      // Recalculate possible win
      calculatePossibleWin(state);
    },

    toggleBetslip: (state) => {
      state.isOpen = !state.isOpen;
    },

    clearBetSlip: (state) => {
      state.bets = [];
      state.totalOdds = 0;
      state.totalStake = 0;
      state.possibleWin = 0;
      state.betType = "single";
      state.systemOptions = [];
      state.errors = [];
    },

    clearErrors: (state) => {
      state.errors = [];
    },
  },
});

// Helper function to update total odds
const updateTotalOdds = (state) => {
  if (state.bets.length === 0) {
    state.totalOdds = 0;
    state.possibleWin = 0;
    return;
  }

  // Calculate based on bet type
  if (state.betType === "single") {
    // For single bets, we use the highest odds
    state.totalOdds = Math.max(
      ...state.bets.map((bet) => parseFloat(bet.odd.value))
    );
  } else if (state.betType === "multiple") {
    // For multiple bets, we multiply all odds
    state.totalOdds = state.bets.reduce(
      (acc, bet) => acc * parseFloat(bet.odd.value),
      1
    );
  }

  // Calculate possible win
  calculatePossibleWin(state);
};

// Helper function to calculate possible win based on bet type
const calculatePossibleWin = (state) => {
  const stake = state.totalStake;

  if (state.betType === "single") {
    // For single bets, divide stake evenly among all selections
    const stakePerBet = stake / state.bets.length;
    state.possibleWin = state.bets.reduce(
      (acc, bet) => acc + stakePerBet * parseFloat(bet.odd.value),
      0
    );
  } else if (state.betType === "multiple") {
    // For multiples, it's just stake * total odds
    state.possibleWin = stake * state.totalOdds;
  } else if (state.betType === "system") {
    // System bets require complex calculation
    // This is a simplified version that should be expanded based on actual system bet requirements
    state.possibleWin = calculateSystemBetWin(
      state.bets,
      state.systemOptions,
      stake
    );
  }

  // Round to 2 decimal places
  state.possibleWin = Math.round(state.possibleWin * 100) / 100;
};

// Helper function to calculate system bet win
// This would need to be expanded to handle real system bet calculations
const calculateSystemBetWin = (bets, systemOptions, stake) => {
  if (!systemOptions.length || !bets.length) return 0;

  // Example: calculating for a 2/3 system bet (all possible doubles from 3 selections)
  const combinationSize = systemOptions[0]; // e.g., 2 for 2/3 system
  const totalCombinations = getCombinationsCount(bets.length, combinationSize);

  // Simple approximation - would need actual combinations calculation for real implementation
  const stakePerCombination = stake / totalCombinations;

  // Calculate average odds as approximation
  const averageOdds =
    bets.reduce((acc, bet) => acc + parseFloat(bet.odd.value), 0) / bets.length;

  // Calculate estimated win using average odds to the power of the combination size
  return (
    stakePerCombination *
    totalCombinations *
    Math.pow(averageOdds, combinationSize)
  );
};

// Helper function to calculate combinations count
const getCombinationsCount = (n, r) => {
  if (r > n) return 0;
  if (r === n || r === 0) return 1;

  let result = 1;
  for (let i = 1; i <= r; i++) {
    result *= n - (r - i);
    result /= i;
  }
  return Math.round(result);
};

export const {
  addBet,
  removeBet,
  setStake,
  setBetType,
  setSystemOptions,
  toggleBetslip,
  clearBetSlip,
  clearErrors,
} = betSlipSlice.actions;

export const betSlipReducer = betSlipSlice.reducer;
